import { writable } from "svelte/store";
import { browser } from "$app/environment";
import { fetchCart } from "#lib/utils/cart.js";
import { getCartAttributes } from "#lib/utils/tracking-attributes.js";
import { captureEvent } from "#lib/utils/posthog.js";
import { isPageHidden } from "#lib/utils/page-hidden.js";
import { getUserCountryPreference } from "#lib/stores/countryStore.js";

function createCartStore() {
  const { subscribe, set, update } = writable({
    id: browser ? localStorage.getItem("cart_id") : null,
    items: [],
    checkoutUrl: null,
    isLoading: false,
    error: null,
    totalAmount: null,
    totalCurrency: null,
    subtotalAmount: null,
    subtotalCurrency: null,
  });

  // Listen for country region changes and reset cart
  if (browser) {
    window.addEventListener("countryRegionChanged", (event) => {
      // console.log('Cart: Region changed, resetting cart', event.detail);
      // Reset the cart state
      set({
        id: null,
        items: [],
        checkoutUrl: null,
        isLoading: false,
        error: null,
        totalAmount: null,
        totalCurrency: null,
        subtotalAmount: null,
        subtotalCurrency: null,
      });
    });
  }

  // Listen for country changes (not region changes) and refetch cart data
  if (browser) {
    window.addEventListener("countryChanged", (event) => {
      // console.log('Cart: Country changed, refetching cart data', event.detail);
      // Refetch cart data to get updated currency from Shopify
      const cartId = localStorage.getItem("cart_id");
      if (cartId) {
        // Use a small delay to ensure the country change has been processed
        setTimeout(() => {
          fetchCart(cartId)
            .then((cart) => {
              const response = { cart };
              if (response?.cart) {
                // console.log('Cart: Refetched cart data after country change:', {
                //   cartTotal: response.cart.cost?.totalAmount,
                //   cartSubtotal: response.cart.cost?.subtotalAmount
                // });

                update((state) => {
                  const newState = {
                    ...state,
                    items: response.cart.lines?.edges || [],
                    checkoutUrl: response.cart.checkoutUrl,
                    totalAmount:
                      response.cart.cost?.totalAmount?.amount || null,
                    totalCurrency:
                      response.cart.cost?.totalAmount?.currencyCode || null,
                    subtotalAmount:
                      response.cart.cost?.subtotalAmount?.amount || null,
                    subtotalCurrency:
                      response.cart.cost?.subtotalAmount?.currencyCode || null,
                    isLoading: false,
                  };

                  // console.log('Cart: Updating cart store with new currency data:', {
                  //   previousCurrency: state.subtotalCurrency,
                  //   newCurrency: newState.subtotalCurrency,
                  //   previousAmount: state.subtotalAmount,
                  //   newAmount: newState.subtotalAmount
                  // });

                  return newState;
                });
              }
            })
            .catch((error) => {
              console.error(
                "Cart: Error refetching cart after country change:",
                error,
              );
            });
        }, 100);
      }
    });
  }

  return {
    subscribe,

    // Initialize or validate cart
    async init() {
      let cartId = browser ? localStorage.getItem("cart_id") : null;

      if (!cartId) {
        // Stamp landing UTMs onto the new cart so they reach the order.
        //
        // Routed through /api/cart/create rather than calling Shopify from the
        // browser: the endpoint is what stamps posthog_distinct_id (the pin
        // that joins the order back to the visitor PostHog saw), and it
        // allowlists the attributes it accepts. A cart created here used to
        // carry a client-built pin, or none at all.
        try {
          const response = await fetch("/api/cart/create", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: getCartAttributes() }),
          });
          const created = response.ok ? await response.json() : null;
          if (!created?.cartId)
            throw new Error(`Failed to create cart (HTTP ${response.status})`);
          cartId = created.cartId;
        } catch (error) {
          // Counted here rather than in Cart.svelte, which used to raise the
          // same event from a catch wrapping BOTH init() and fetch() with no
          // property telling them apart. That ambiguity is why the 28-day
          // reading of this finding could only be an upper bound
          // (hub ledger 2026-09-23-069).
          captureEvent("cart_init_failed", {
            stage: "create",
            reason: error?.message ?? String(error),
            page_hidden: isPageHidden(),
          });
          throw error;
        }
        if (browser) {
          localStorage.setItem("cart_id", cartId);
        }
      }

      // Sync buyer country on every init (not just cart creation) so re-adding
      // after a manual country switch also re-points Markets at the newly
      // selected region, not whatever country the cart last had.
      const countryCode = browser ? getUserCountryPreference() : null;
      if (countryCode) {
        try {
          // Through our own origin, not the Storefront API from the page: the
          // myshopify.com domain is refused outright by iOS content blockers
          // and the Instagram / Facebook in-app webviews, and Shopify serves
          // its bot challenge to the shopper's IP (hub ledger 2026-09-23-069).
          const response = await fetch("/api/cart/buyer-identity", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ cartId, countryCode }),
          });
          if (!response.ok)
            throw new Error(
              `Failed to set buyer identity (HTTP ${response.status})`,
            );
        } catch (error) {
          console.error("Cart: failed to set buyer identity", error);
          captureEvent("cart_buyer_identity_failed", {
            reason: error?.message ?? String(error),
            page_hidden: isPageHidden(),
          });
        }
      }

      set({
        id: cartId,
        items: [],
        checkoutUrl: null,
        isLoading: false,
        error: null,
        totalAmount: null,
        totalCurrency: null,
        subtotalAmount: null,
        subtotalCurrency: null,
      });

      return cartId;
    },

    // Fetch latest cart data
    async fetch() {
      update((state) => ({ ...state, isLoading: true, error: null }));

      try {
        const cartId = browser ? localStorage.getItem("cart_id") : null;
        if (!cartId) {
          // console.log('🔄 Cart fetch: No cart ID found');
          return;
        }

        // console.log('🔄 Cart fetch: Fetching cart data for ID:', cartId);
        const cart = await fetchCart(cartId);
        const response = { cart };
        if (response?.cart) {
          // console.log('🔄 Cart fetch: Processing cart data:', {
          //   cartId,
          //   itemCount: response.cart.lines?.edges?.length || 0,
          //   cartTotal: response.cart.cost?.totalAmount,
          //   cartSubtotal: response.cart.cost?.subtotalAmount,
          //   checkoutUrl: response.cart.checkoutUrl
          // });

          update((state) => ({
            ...state,
            items: response.cart.lines?.edges || [],
            checkoutUrl: response.cart.checkoutUrl,
            totalAmount: response.cart.cost?.totalAmount?.amount || null,
            totalCurrency:
              response.cart.cost?.totalAmount?.currencyCode || null,
            subtotalAmount: response.cart.cost?.subtotalAmount?.amount || null,
            subtotalCurrency:
              response.cart.cost?.subtotalAmount?.currencyCode || null,
            isLoading: false,
          }));
        }
      } catch (error) {
        console.error("🔄 Cart fetch error:", error);
        // PostHog: cart data fetch failed - the drawer/totals can't render and
        // checkout has no fresh state. captureEvent no-ops server-side / when
        // PostHog isn't loaded, so this is safe in the store module.
        // `stage` is why this event can be read at all. It is raised from more
        // than one call, and without it the 28-day count could only be quoted
        // as an upper bound - 19 people, some share of them the same-origin
        // create rather than the Shopify read (hub ledger 2026-09-23-069).
        captureEvent("cart_init_failed", {
          stage: "fetch",
          reason: error?.message ?? String(error),
          page_hidden: isPageHidden(),
        });
        update((state) => ({
          ...state,
          error: error.message,
          isLoading: false,
        }));
      }
    },

    // Reset cart state
    reset() {
      if (browser) {
        localStorage.removeItem("cart_id");
      }
      set({
        id: null,
        items: [],
        checkoutUrl: null,
        isLoading: false,
        error: null,
        totalAmount: null,
        totalCurrency: null,
        subtotalAmount: null,
        subtotalCurrency: null,
      });
    },
  };
}

export const cartStore = createCartStore();
